'use client';

import { ReactNode, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { apiService } from "../../../services/ApiService";
import { ILocalizedParams, IPaginatedData } from "../../../models/ICommon";
import { ISection } from '../../../models/ISection';
import PostsSection from '../../sections/PostsSection';
import HomepageBanner from '../Banner';
import Button, { ButtonTheme } from '../Button';


const templateComponentsMap: (props: any) => ({ [templateId: string]: ReactNode }) = (props: any) => ({
  6: <HomepageBanner {...props} key={props.key} />,
  7: <PostsSection {...props} className='home-posts-1' key={props.key} />, // 3 streched
  8: <PostsSection {...props} className='home-posts-2' key={props.key} />, // 2 row, 2small vertical 2 big horizontal
  9: <PostsSection {...props} className='home-posts-3' key={props.key} />, // 2x3
  10: <PostsSection {...props} className='home-posts-4' key={props.key} />, // 3 streched x 3
  11: <PostsSection {...props} className='home-posts-5' key={props.key} />, // 3 x 4
  2: <PostsSection {...props} className='home-posts-6' key={props.key} />, // 1 big
  // 12 // 3x3 
});


const locales = {
  ar: {
    button: 'المزيد',
    loading: 'تحميل'
  },
  en: {
    button: 'More',
    loading: 'Loading'
  },
}
interface Props {
  initialSections: IPaginatedData<ISection>;
  locale: string;
  device: string | null;
}

export default function HomeSections({ initialSections, locale = 'ar', device }: ILocalizedParams<Props>) {

  const isDesktop = device === 'desktop';

  const {
    data,
    isFetching,
    isLoading,
    fetchNextPage,
    hasNextPage
  } = useInfiniteQuery({
    retry: false,
    initialData: () => {
      return {
        pageParams: [undefined, 1],
        pages: [initialSections]
      }
    },
    enabled: false,
    queryKey: [locale, 'home-sections'],
    queryFn: async ({ pageParam = 1 }) => {
      const result = await apiService.getHomeSections({ page: pageParam as number, pageSize: 1 });
      return result;
    },
    getNextPageParam: (lastPage, allPages, lastPageParam, allPagesParam) => {
      if (!lastPage) return 1;
      return lastPage.page < lastPage.totalPages ? lastPage.page + lastPage.pageSize : undefined;
    },
    initialPageParam: 1
  });

  const totalData = useMemo(
    () => (data?.pages ? data.pages.map(item => item.data).flat(Infinity) : []),
    [data]
  ) as ISection[];

  const observer = useRef<IntersectionObserver>();
  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetching) {
          fetchNextPage();
        }
      }, { threshold: 0.5 });
      if (node) observer.current.observe(node);
    },
    [isLoading, fetchNextPage, hasNextPage, isFetching]
  );

  return (
    <div className="flex flex-col">

      <div className='flex flex-col w-full'>
        {totalData.map((s, i) => {
          const eager = i === 0;
          return (
          templateComponentsMap(
            {
              ...s,
              device,
              posts: s.posts.map(p => ({ ...p, key: p.uniqueCode })),
              key: `${i}_${s.id}`,
              locale,
              ref: totalData.length === i + 1 ? lastElementRef : null,
              eager
            })[s.template.id]
        )
          })}
      </div>
      {isLoading || isFetching ?
        <div className='w-full grid place-items-center string-18 my-[60px]'>{locales[locale].loading}...</div> :
        (
          hasNextPage ?
          <div className='w-full desktop:w-[520px] mt-[60px] h-[60px] px-[24px] flex justify-center items-center mx-auto'>
            <Button locale={locale} title={locales[locale].button} theme={ButtonTheme.primary} className='h-full' showIcon onClick={() => fetchNextPage()} />
              
              </div> :
            null
        )
      }

    </div>
  )



}